<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import {
		investmentManagementFormSchema,
		investmentAmountOptions,
		investmentTimelineOptions
	} from "./investment-management-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { toast } from "svelte-sonner";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { dev } from "$app/env";
	import { TurnstileField } from "./turnstile";

	const form = superForm(defaults(zod4(investmentManagementFormSchema)), {
		id: "investment-management",
		SPA: true,
		resetForm: true,
		validators: zod4(investmentManagementFormSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitInvestmentManagement.mutate(form.data);
			} catch (e) {
				form.data.turnstileToken = "";
				toast.error(
					e instanceof Error ? e.message : "Failed to submit inquiry. Please try again."
				);
				return;
			}

			toast.success("Investment inquiry submitted successfully!");
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	let smsMarketingOptIn = $state(false);
	let smsTransactionalOptIn = $state(false);

	if (dev) {
		$formData.firstName = "John";
		$formData.lastName = "Doe";
		$formData.email = "john.doe@example.com";
		$formData.phone = "(123) 456-7890";
		$formData.investmentAmount = "$100,000 - $250,000";
		$formData.investmentTimeline = "1-3 Months";
		$formData.howDidYouHearAboutUs = "Google Search";
		$formData.iAmAnAccreditedInvestor = true;
		$formData.message = "I have questions about your investment opportunities...";
		$formData.agreedToTermsAndPrivacy = true;
	}
</script>

<form use:enhance method="POST" class="w-full space-y-6">
	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-2.5">
			<Label for="firstName">First Name</Label>
			<Input
				id="firstName"
				name="firstName"
				type="text"
				bind:value={$formData.firstName}
				autocomplete="given-name"
			/>
			{#if $errors.firstName}
				<p class="text-destructive text-sm">{$errors.firstName}</p>
			{/if}
		</div>

		<div class="space-y-2.5">
			<Label for="lastName">Last Name</Label>
			<Input
				id="lastName"
				name="lastName"
				type="text"
				bind:value={$formData.lastName}
				autocomplete="family-name"
			/>
			{#if $errors.lastName}
				<p class="text-destructive text-sm">{$errors.lastName}</p>
			{/if}
		</div>
	</div>

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-2.5">
			<Label for="email">Email</Label>
			<Input
				id="email"
				name="email"
				type="email"
				bind:value={$formData.email}
				autocomplete="email"
			/>
			{#if $errors.email}
				<p class="text-destructive text-sm">{$errors.email}</p>
			{/if}
		</div>

		<div class="space-y-2.5">
			<Label for="phone">Phone</Label>
			<Input
				id="phone"
				name="phone"
				type="tel"
				bind:value={$formData.phone}
				placeholder="(123) 456-7890"
				autocomplete="tel"
				onchange={(e) => {
					const v = e.currentTarget.value;
					$formData.phone = v ? formatPhoneNumber(v) : "";
				}}
			/>
			{#if $errors.phone}
				<p class="text-destructive text-sm">{$errors.phone}</p>
			{/if}
		</div>
	</div>

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-2.5">
			<Label for="investmentTimeline">Investment Timeline</Label>
			<select
				id="investmentTimeline"
				name="investmentTimeline"
				bind:value={$formData.investmentTimeline}
				class="border-input ring-offset-background focus-visible:ring-ring bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				<option value="">Select an option</option>
				{#each investmentTimelineOptions as option}
					<option value={option}>{option}</option>
				{/each}
			</select>
			{#if $errors.investmentTimeline}
				<p class="text-destructive text-sm">{$errors.investmentTimeline}</p>
			{/if}
		</div>

		<div class="space-y-2.5">
			<Label for="investmentAmount">Investment Amount</Label>
			<select
				id="investmentAmount"
				name="investmentAmount"
				bind:value={$formData.investmentAmount}
				class="border-input ring-offset-background focus-visible:ring-ring bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
			>
				<option value="">Select an option</option>
				{#each investmentAmountOptions as option}
					<option value={option}>{option}</option>
				{/each}
			</select>
			{#if $errors.investmentAmount}
				<p class="text-destructive text-sm">{$errors.investmentAmount}</p>
			{/if}
		</div>
	</div>

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end">
		<div class="space-y-2.5">
			<Label for="howDidYouHearAboutUs">How did you hear about us?</Label>
			<Input
				id="howDidYouHearAboutUs"
				name="howDidYouHearAboutUs"
				type="text"
				bind:value={$formData.howDidYouHearAboutUs}
			/>
			{#if $errors.howDidYouHearAboutUs}
				<p class="text-destructive text-sm">{$errors.howDidYouHearAboutUs}</p>
			{/if}
		</div>

		<div class="flex items-center space-x-2 pb-0.5">
			<input
				id="iAmAnAccreditedInvestor"
				name="iAmAnAccreditedInvestor"
				type="checkbox"
				bind:checked={$formData.iAmAnAccreditedInvestor}
				class="border-input ring-offset-background focus-visible:ring-ring h-4 w-4 rounded border"
			/>
			<Label for="iAmAnAccreditedInvestor" class="cursor-pointer text-sm font-normal">
				I am an accredited investor
			</Label>
			{#if $errors.iAmAnAccreditedInvestor}
				<p class="text-destructive text-sm">{$errors.iAmAnAccreditedInvestor}</p>
			{/if}
		</div>
	</div>

	<div class="space-y-2.5">
		<Label for="message">Message</Label>
		<Textarea
			class="bg-background w-full"
			name="message"
			bind:value={$formData.message}
			placeholder="Tell us about your investment goals and any questions you have..."
		/>
		{#if $errors.message}
			<p class="text-destructive text-sm">{$errors.message}</p>
		{/if}
	</div>

	<div class="border-input bg-background/50 space-y-3 rounded-md border p-4">
		<div class="flex items-start gap-2">
			<input
				id="smsMarketingOptIn"
				type="checkbox"
				bind:checked={smsMarketingOptIn}
				class="border-input ring-offset-background focus-visible:ring-ring mt-0.5 h-4 w-4 shrink-0 rounded border"
			/>
			<Label for="smsMarketingOptIn" class="cursor-pointer text-sm leading-snug font-normal">
				I agree to receive marketing text messages from Fergmar Capital about investment
				opportunities, events, and related updates. Message frequency may vary. Message and
				data rates may apply. Reply STOP to unsubscribe or HELP for help.
			</Label>
		</div>

		<div class="flex items-start gap-2">
			<input
				id="smsTransactionalOptIn"
				type="checkbox"
				bind:checked={smsTransactionalOptIn}
				class="border-input ring-offset-background focus-visible:ring-ring mt-0.5 h-4 w-4 shrink-0 rounded border"
			/>
			<Label
				for="smsTransactionalOptIn"
				class="cursor-pointer text-sm leading-snug font-normal"
			>
				I agree to receive non-marketing text messages from Fergmar Capital related to my
				inquiry, appointment confirmations, reminders, and follow-up communications. Message
				frequency may vary. Message and data rates may apply. Reply STOP to unsubscribe or
				HELP for help.
			</Label>
		</div>
	</div>

	<div class="space-y-2">
		<div class="flex items-start gap-2">
			<input
				id="agreedToTermsAndPrivacy"
				name="agreedToTermsAndPrivacy"
				type="checkbox"
				bind:checked={$formData.agreedToTermsAndPrivacy}
				class="border-input ring-offset-background focus-visible:ring-ring mt-0.5 h-4 w-4 shrink-0 rounded border"
			/>
			<Label
				for="agreedToTermsAndPrivacy"
				class="cursor-pointer text-sm leading-snug font-normal"
			>
				I agree to the
				<a href="/privacy-policy" class="hover:text-foreground underline">Privacy Policy</a>
				and

				<a href="/terms-and-conditions" class="hover:text-foreground underline"
					>Terms & Conditions</a
				>

				.
			</Label>
		</div>
		{#if $errors.agreedToTermsAndPrivacy}
			<p class="text-destructive text-sm">{$errors.agreedToTermsAndPrivacy}</p>
		{/if}
	</div>

	<div>
		<TurnstileField bind:token={$formData.turnstileToken} class="mb-4" />
	</div>

	<p class="text-muted-foreground text-xs leading-relaxed">
		Consent to receive SMS messages is not a condition of purchase or investment.
	</p>

	<Button
		type="submit"
		disabled={$submitting}
		aria-busy={$submitting}
		class="bg-primary text-primary-foreground hover:bg-primary/80 h-10 w-full rounded text-base font-semibold"
	>
		{$submitting ? "Submitting..." : "Submit"}
	</Button>
</form>
